import classnames from 'classnames';
import React, { useEffect, useImperativeHandle, useRef } from 'react';

import styles from './InputField.module.css';

import { ErrorMessage, InputLabel } from '@/shared/components/atoms';

import { IInputFieldProps } from './IInputField.d';
import { BaseIcon } from '../IconButton/BaseIcon';

const InputField: React.FC<IInputFieldProps> = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  IInputFieldProps
>(
  (
    {
      label,
      className,
      errorClasseName,
      labelClasseName,
      id,
      prefix,
      suffix,
      error,
      errorMessage,
      disabled,
      customDisabledStyle,
      multiline,
      py,
      px,
      width,
      textColor,
      fontSize,
      borderColor,
      iconSize,
      iconColor,
      placeholderColor,
      rows = 3,
      ...props
    },
    ref
  ) => {
    const inputClasses = classnames(
      styles['input-field'],
      {
        [styles['error']]: error,
        [styles['disabled']]: disabled && customDisabledStyle === undefined,
        // [styles['prefix-padding']]: prefix,
        // [styles['suffix-padding']]: suffix,
        [styles['default-padding']]: !prefix && !suffix,
      },
      className,
      disabled ? customDisabledStyle : '',
      py,
      px,
      placeholderColor,
      fontSize,
      borderColor,
      textColor
    );
    const inputProps = {
      ...props,
    } as React.InputHTMLAttributes<HTMLInputElement>;
    const textareaProps = {
      ...props,
    } as React.TextareaHTMLAttributes<HTMLTextAreaElement>;

    const textareaClasses = classnames(inputClasses, styles['textarea']);

    // Define effect to calculate padding automatically based on prefix and suffix
    const inputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const prefixRef = useRef<HTMLDivElement>(null); // Ref for prefix
    const suffixRef = useRef<HTMLDivElement>(null); // Ref for suffix

    useImperativeHandle(
      ref,
      () =>
        (multiline ? textareaRef.current : inputRef.current) as
          | HTMLInputElement
          | HTMLTextAreaElement
    );
    useEffect(() => {
      const element = multiline ? textareaRef.current : inputRef.current;
      if (element) {
        let paddingLeft = '16px';
        let paddingRight = '16px';

        if (prefix && prefixRef.current) {
          paddingLeft = `${prefixRef.current.offsetWidth + 20}px`;
        }

        if (suffix && suffixRef.current) {
          paddingRight = `${suffixRef.current.offsetWidth + 20}px`;
        }

        const direction = window.getComputedStyle(element).direction;
        const padding =
          direction === 'rtl'
            ? `9px ${paddingLeft} 9px ${paddingRight}`
            : `9px ${paddingRight} 9px ${paddingLeft}`;

        element.style.padding = padding;
      }
    }, [prefix, suffix, multiline]);

    return (
      <div className={`relative flex flex-col ${width}`}>
        {label ? (
          <InputLabel
            label={label}
            className={labelClasseName}
            disabled={disabled}
            id={id}
            required={props.required}
          />
        ) : // <label htmlFor={id} className={labelClasses}>
        //   <Typography variant='body1' weight='regular'>
        //     {label}{' '}
        //     {props?.required && <span className={styles['required']}>*</span>}
        //   </Typography>
        // </label>
        null}
        <div className='relative w-full'>
          {prefix ? (
            <BaseIcon
              size={iconSize}
              svgClasses={`${iconColor} ${styles['icon']}  ${styles['prefix']}`}
            >
              <div ref={prefixRef}> {prefix} </div>
            </BaseIcon>
          ) : null}
          {suffix ? (
            <BaseIcon
              size={iconSize}
              svgClasses={`${iconColor} ${styles['icon']}  ${styles['suffix']}`}
            >
              <div ref={suffixRef}>{suffix}</div>
            </BaseIcon>
          ) : null}
          {multiline ? (
            <textarea
              ref={textareaRef}
              id={id}
              className={textareaClasses}
              rows={rows}
              {...textareaProps}
            />
          ) : (
            <input
              ref={inputRef}
              id={id}
              className={inputClasses}
              onWheel={(e) => {
                // prevent changing value of number input on scroll
                if (props.type === 'number') {
                  (e.target as HTMLInputElement).blur();
                  e.stopPropagation();
                }
              }}
              {...inputProps}
            />
          )}
        </div>
        {errorMessage && error ? (
          <ErrorMessage
            errorMessage={errorMessage}
            className={errorClasseName}
          />
        ) : null}
      </div>
    );
  }
);

export { InputField };

InputField.defaultProps = {
  py: '',
  px: '',
  textColor: 'text-text-default',
  fontSize: 'text-base',
  borderColor: 'border-background-disable',
  iconSize: 'sm',
  iconColor: 'fill-text-600',
  placeholderColor: 'placeholder-text-secondary',
};
